import { motion } from "framer-motion";
import {
  BedDouble,
  BedSingle,
  Sofa,
  CookingPot,
  UtensilsCrossed,
  ShowerHead,
  WashingMachine,
  Wifi,
  AirVent,
  Bike,
  Flame,
  FlameKindling,
  Cherry,
  Sparkles,
  Octagon,
  Waves,
} from "lucide-react";
import { EASE } from "@/lib/scroll";

const AMENITIES = [
  { icon: BedDouble, title: "Master Bedroom", desc: "Queen bed, breeze-through windows & ceiling fan" },
  { icon: BedSingle, title: "Second Bedroom", desc: "Twin-over-single bunks, oversized headroom below" },
  { icon: Octagon, title: "The Octagon Room", desc: "Eight-sided room: books, games, desk & streaming TV" },
  { icon: Sofa, title: "Sleeping Nook", desc: "Extra sleeping area for bigger crews" },
  { icon: CookingPot, title: "Full IKEA Kitchen", desc: "Stove, microwave, coffee maker & air fryer" },
  { icon: UtensilsCrossed, title: "Two Eating Areas", desc: "Kitchen nook plus deck dining" },
  { icon: ShowerHead, title: "Outdoor Shower", desc: "Private behind a locked door, under sun or stars" },
  { icon: WashingMachine, title: "Washer & Dryer", desc: "In the fully renovated washroom" },
  { icon: Wifi, title: "High-Speed Internet", desc: "Work-from-cottage friendly" },
  { icon: AirVent, title: "Window AC", desc: "Takes the bite out of a hot day" },
  { icon: FlameKindling, title: "Wood Fireplace", desc: "Firewood ready for chilly evenings" },
  { icon: Bike, title: "Bikes & Paths", desc: "A short ride to all of Victoria Beach" },
  { icon: Flame, title: "BBQ & Private Deck", desc: "Room for gatherings, wrapped in greenery" },
  { icon: Sparkles, title: "Screen Room", desc: "Nap sofa, dining table & upright freezer" },
  { icon: Cherry, title: "Wild Berry Picking", desc: "Raspberries, chokecherries & saskatoons in season" },
  { icon: Waves, title: "Steps to the Sand", desc: "Connaught Beach is 300 steps away" },
];

export default function Amenities() {
  return (
    <section
      id="amenities"
      data-testid="amenities-section"
      className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-36"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-16 max-w-2xl"
      >
        <div className="mb-6 flex items-center gap-4">
          <span className="h-px w-12 bg-[#C8634B]" />
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C8634B]">
            Amenities
          </p>
        </div>
        <h2 className="font-serif text-3xl leading-snug tracking-tight text-[#1E3323] sm:text-4xl lg:text-5xl">
          Old bones, <span className="italic text-[#C8634B]">modern comforts.</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {AMENITIES.map((item, i) => (
          <motion.div
            key={item.title}
            data-testid={`amenity-card-${i + 1}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: EASE }}
            className="group rounded-xl border border-[#1E3323]/12 bg-white/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#C8634B]/50 hover:bg-white hover:shadow-lg hover:shadow-[#1E3323]/5"
          >
            <span className="mb-4 block h-0.5 w-8 bg-[#C8634B] transition-all duration-300 group-hover:w-12" />
            <h3 className="font-sans text-base font-semibold tracking-wide text-[#1E3323]">
              {item.title}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[#767970]">{item.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
